import { makeStyles } from "@material-ui/core";
import { useSelector, useDispatch } from "react-redux";
import React, { memo, useMemo, useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router-dom";
import { notify } from "../../../actions/notification";
import { Save, DeleteForever } from "../../../icons";
import { saveKeyword } from "../../../actions/keywords";
import {
  Page,
  Box,
  Grid,
  TextField,
  MenuItem,
  Typography,
  Card,
  CardContent,
  ErrorButtonContained,
  PrimaryButtonContained,
} from "../../../components";
import { globalClasses } from "../../../components/ThemeProvider";

const useStyles = makeStyles((theme) => ({
  content: {
    width: "100%",
    overflow: "auto",
  },
  root: {
    padding: `0 ${theme.spacing(2)}px`,
    backgroundColor: theme.palette.grey[200],
    marginTop: "auto",
    marginBottom: 0,
  },
}));

const getShortCodes = ({
  sinchShortCodes = [],
  xmlShortCodes = [],
  jasminShortCodes = [],
}) => [...sinchShortCodes, ...xmlShortCodes, ...jasminShortCodes];

function CreateKeyword() {
  const { t } = useTranslation();
  const classes = useStyles();
  const dispatch = useDispatch();
  const history = useHistory();
  const queryParams = useSelector((state) => state.params);
  // const isSmallScreen = useMediaQuery((theme) => theme.breakpoints.down("xs"));

  const { activeDivisionId, paid } = useSelector((state) => state.storage);

  const { smsConfig, emailConfig, vendorConfig } = useSelector(
    (state) => state.eonsConfig
  );
  const { errors = {} } = useSelector((state) =>
    state.keywords ? state.keywords : {}
  );
  const [keyword, setKeyword] = useState({});

  const { shortCode = "", kword, response } = keyword;
  const shortCodes = useMemo(() => getShortCodes(smsConfig), [smsConfig]);

  const callerIds = useMemo(() => {
    return vendorConfig.callerId;
  }, [vendorConfig]);

  const onChange = (ev) => {
    const { name, value } = ev.target;
    setKeyword({
      ...keyword,
      [name]: value,
    });
  };

  const onSave = useCallback(async () => {
    try {
      await dispatch(
        saveKeyword({
          divisionId: activeDivisionId,
          paid: "" + paid,
          keyword: keyword.keyword,
          value: keyword.value,
          shortcode: keyword.shortCode,
        })
      );
      history.replace(`/keyword`);
    } catch (err) {
      if (err.message?.indexOf("400") === -1) {
        dispatch(
          notify({
            type: "error",
            message: err.message,
          })
        );
      }
    }
  }, [dispatch, shortCode, keyword, history]);

  const onCancel = useCallback(() => history.replace("/keyword"), [history]);

  const addWordsToKeywordList = (
    <Card>
      <CardContent>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={12} md={12} lg={12} xl={12} color="primary">
            <Typography gutterBottom variant="h4" align="left">
              Add New Keyword
            </Typography>
          </Grid>
          <Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
            <Card>
              <CardContent>
                <Grid
                  container
                  spacing={4}
                  justify="space-between"
                  alignItems="center"
                >
                  <Grid item xs={12} sm={12} md={6}>
                    <TextField
                      data-test-id="keyword-create-shortcode-field"
                      select
                      label={t("Short Code")}
                      variant="outlined"
                      size="small"
                      fullWidth
                      name="shortCode"
                      value={shortCode}
                      onChange={onChange}
                      error={Boolean(errors["shortCode"])}
                      helperText={
                        Boolean(errors["shortCode"])
                          ? t("must be a valid shortcode")
                          : ""
                      }
                      className={globalClasses.filterPaper}
                    >
                      {shortCodes.map((shortCode) => (
                        <MenuItem
                          key={shortCode}
                          value={shortCode}
                          data-test-id={`shortcode-id-${shortCode}`}
                        >
                          {shortCode}
                        </MenuItem>
                      ))}
                    </TextField>
                  </Grid>
                  <Grid item xs={12} sm={12} md={6}>
                    <TextField
                      data-test-id="keyword-create-keyword-field"
                      label={t("Keyword")}
                      variant="outlined"
                      size="small"
                      fullWidth
                      name="keyword"
                      value={kword}
                      onChange={onChange}
                      error={Boolean(errors["keyword"])}
                      helperText={
                        Boolean(errors["keyword"]) ? t("must not be empty") : ""
                      }
                      className={globalClasses.filterPaper}
                    />
                  </Grid>
                  <Grid item xs={12} sm={12} md={12} lg={8} xl={4}>
                    <TextField
                      data-test-id="keyword-create-response-field"
                      multiline
                      rows={4}
                      rowsMax={8}
                      label={t("Response")}
                      variant="outlined"
                      size="small"
                      fullWidth
                      name="value"
                      value={response}
                      onChange={onChange}
                      error={Boolean(errors["value"])}
                      helperText={
                        Boolean(errors["value"]) ? t("must not be empty") : ""
                      }
                      className={globalClasses.filterPaper}
                    />
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  );

  return (
    <Page screenSized>
      <Box p={2} className={classes.content}>
        {addWordsToKeywordList}
      </Box>
      <Grid
        container
        spacing={2}
        alignItems="center"
        justify="flex-end"
        className={classes.root}
      >
        <Grid item>
          <ErrorButtonContained
            data-test-id="keyword-create-cancel-button"
            startIcon={<DeleteForever />}
            onClick={onCancel}
            disabled={false}
          >
            {t("Cancel")}
          </ErrorButtonContained>
        </Grid>
        <Grid item>
          <PrimaryButtonContained
            data-test-id="keyword-create-save-button"
            startIcon={<Save />}
            onClick={onSave}
            disabled={false}
            error={false}
          >
            {t("Save")}
          </PrimaryButtonContained>
        </Grid>
      </Grid>
    </Page>
  );
}

export default memo(CreateKeyword);
